import { useCallback, useEffect, useRef, useState } from "react";

import {
    Disclosure,
    DisclosurePlacement,
    DisclosurePlayContext,
    clipId,
    hasClip,
    requiresDisclosure,
} from "JS/Models/Disclosure";
import { isSameMediaSource } from "JS/Helpers/S3Helper";
import { useDisclosureAnalytics } from "./DisclosureAnalytics";

export type DisclosureStep = "start" | "content" | "end";

export interface DisclosureSequenceOptions {
    contentUrl: string;
    disclosure?: Disclosure;
    startUrl?: string;
    endUrl?: string;
    skipStart?: boolean;
    resolving?: boolean;
    loop?: boolean;
    autoPlay?: boolean;
    advanceOnComplete?: boolean;
    analytics?: DisclosurePlayContext;
}

const END_OF_CLIP_MS = 400;

const buildSteps = (o: DisclosureSequenceOptions): DisclosureStep[] => {
    const openWith =
        hasClip(o.disclosure?.start) && !o.skipStart && !!o.startUrl;
    const closeWith = hasClip(o.disclosure?.end) && !!o.endUrl;

    return [
        ...(openWith ? (["start"] as DisclosureStep[]) : []),
        "content" as DisclosureStep,
        ...(closeWith ? (["end"] as DisclosureStep[]) : []),
    ];
};

// an unresolved clip is not "no clip": hold the content until we know
const awaitingStartClip = (o: DisclosureSequenceOptions) =>
    !!o.resolving && hasClip(o.disclosure?.start) && !o.skipStart && !o.startUrl;

const withLockedSkip = (
    o: DisclosureSequenceOptions,
    locked: boolean | null,
) => (locked === null ? o : { ...o, skipStart: locked });

const skipsStartClip = (o: DisclosureSequenceOptions) =>
    !!o.skipStart && hasClip(o.disclosure?.start) && !!o.startUrl;

export const useDisclosureSequence = (options: DisclosureSequenceOptions) => {
    const { contentUrl, disclosure, startUrl, endUrl, skipStart } = options;

    const [step, setStep] = useState<DisclosureStep | null>(null);
    const [playing, setPlaying] = useState(!!options.autoPlay);
    const [clipFailed, setClipFailed] = useState(false);
    const [stepToken, setStepToken] = useState(0);

    const wasAutoPlay = useRef(!!options.autoPlay);
    useEffect(() => {
        const nowAutoPlay = !!options.autoPlay;
        if (nowAutoPlay && !wasAutoPlay.current) setPlaying(true);
        wasAutoPlay.current = nowAutoPlay;
    }, [options.autoPlay]);

    const optionsRef = useRef(options);
    optionsRef.current = options;
    const stepRef = useRef<DisclosureStep | null>(null);
    const skipLock = useRef<boolean | null>(null);

    const pauseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const settling = useRef(false);

    const { recordPlayed } = useDisclosureAnalytics();
    const recordPlayedRef = useRef(recordPlayed);
    recordPlayedRef.current = recordPlayed;
    const playedKeyRef = useRef<string | null>(null);
    // the source that was live when the current step's media actually started
    const startedForRef = useRef<string | null>(null);

    const clearPauseTimer = () => {
        if (pauseTimer.current) clearTimeout(pauseTimer.current);
        pauseTimer.current = null;
    };

    useEffect(() => clearPauseTimer, []);

    const sourceRef = useRef(contentUrl);
    const [source, setSource] = useState(contentUrl);

    const isNewSource = !!contentUrl && !isSameMediaSource(source, contentUrl);

    if (isNewSource) {
        setSource(contentUrl);
        sourceRef.current = contentUrl;
        clearPauseTimer();
        stepRef.current = null;
        startedForRef.current = null;
        playedKeyRef.current = null;
        skipLock.current = null;
        setStepToken((prev) => prev + 1);
        if (step !== null) setStep(null);
        if (clipFailed) setClipFailed(false);
    }

    const goTo = useCallback(
        (next: DisclosureStep | null, keepPlaying: boolean) => {
            stepRef.current = next;
            // the src is about to change: the new media must prove itself again
            startedForRef.current = null;
            setStep(next);
            setStepToken((prev) => prev + 1);
            setPlaying(keepPlaying);
            settling.current = keepPlaying;
            if (next === null) playedKeyRef.current = null;
        },
        [],
    );

    const handleEnded = useCallback(() => {
        clearPauseTimer();

        const live = withLockedSkip(optionsRef.current, skipLock.current);
        const steps = buildSteps(live);
        const startSkipped = skipsStartClip(live);
        const owned =
            stepRef.current !== null ||
            (steps.length === 1 && !startSkipped) ||
            startedForRef.current === sourceRef.current;

        const current = stepRef.current ?? steps[0];
        const next = steps[steps.indexOf(current) + 1];

        if (!owned) return false;

        if (next) {
            goTo(next, true);
            return false;
        }

        // one step is not a sequence: report completion and leave the player alone
        if (steps.length === 1 && !startSkipped) return true;

        skipLock.current = false;

        // only own the loop while a sequence is running: with no clip the
        // player loops natively, exactly as it did before this feature
        if (optionsRef.current.loop) {
            goTo(null, true);
            return false;
        }

        if (optionsRef.current.advanceOnComplete) {
            goTo(null, true);
            return true;
        }

        goTo(null, false);
        return true;
    }, [goTo]);

    const onError = useCallback(() => {
        const placement =
            stepRef.current ??
            buildSteps(withLockedSkip(optionsRef.current, skipLock.current))[0];

        if (placement === "content") return;

        clearPauseTimer();
        setPlaying(false);
        setClipFailed(true);
    }, []);

    const onPlay = useCallback(() => {
        clearPauseTimer();
        settling.current = false;
        startedForRef.current = sourceRef.current;
        if (skipLock.current === null)
            skipLock.current = !!optionsRef.current.skipStart;
        setPlaying(true);

        try {
            const live = optionsRef.current;
            const placement =
                stepRef.current ??
                buildSteps(withLockedSkip(live, skipLock.current))[0];
            if (placement === "content" || !live.analytics) return;

            const disclosureId = clipId(
                placement === "start"
                    ? live.disclosure?.start
                    : live.disclosure?.end,
            );
            if (!disclosureId) return;

            const key = [
                placement,
                disclosureId,
                live.contentUrl,
                live.analytics.skuId,
                live.analytics.nId,
            ].join("|");
            if (playedKeyRef.current === key) return;
            playedKeyRef.current = key;

            recordPlayedRef.current({
                ...live.analytics,
                disclosureId,
                placement: placement as DisclosurePlacement,
            });
        } catch {
            // analytics must never reach playback
        }
    }, []);

    const onPause = useCallback(() => {
        if (settling.current) return;
        const live = withLockedSkip(optionsRef.current, skipLock.current);
        // the hold paused the player, not the member
        if (awaitingStartClip(live)) return;

        const steps = buildSteps(live);
        const owned =
            stepRef.current !== null ||
            steps.length === 1 ||
            startedForRef.current === sourceRef.current;

        if (!owned) return;

        clearPauseTimer();
        pauseTimer.current = setTimeout(() => {
            pauseTimer.current = null;
            setPlaying(false);
        }, END_OF_CLIP_MS);
    }, []);

    const pause = useCallback(() => {
        clearPauseTimer();
        settling.current = false;
        setPlaying(false);
    }, []);

    // state only: a scene re-arming playback must not replay the finished item's analytics
    const resume = useCallback(() => {
        clearPauseTimer();
        settling.current = false;
        setPlaying(true);
    }, []);

    const effective = withLockedSkip(options, skipLock.current);
    const steps = buildSteps(effective);

    const held = isNewSource ? null : step;
    const current = held && steps.includes(held) ? held : steps[0];

    const url =
        current === "start"
            ? startUrl
            : current === "end"
              ? endUrl
              : contentUrl;

    const isMissingMedia =
        requiresDisclosure(disclosure) &&
        ((hasClip(disclosure?.start) && !skipStart && !startUrl) ||
            (hasClip(disclosure?.end) && !endUrl));

    return {
        step: current,
        stepToken,
        url,
        isDisclosure: current !== "content",
        isSequencing: steps.length > 1 || skipsStartClip(effective),
        isMissingMedia,
        clipFailed,
        playing: playing && !awaitingStartClip(effective),
        onError,
        handleEnded,
        onPlay,
        onPause,
        pause,
        resume,
    };
};
